import clsx from 'clsx';
import type { EmblaViewportRefType } from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from '@/components/pages/icons';
import { SlideItem } from './item';
import { defaultContent } from '../constants';
import type { HistoryProps } from '../type';
import { Block } from '../../block';

type SlideServerProps = HistoryProps & {
  emblaRef?: EmblaViewportRefType;
  canScrollPrev?: boolean;
  canScrollNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
};

const { title: defaultTitle, items: defaultItems } = defaultContent.slide;

export function SlideServer({
  title = defaultTitle,
  items = defaultItems,
  emblaRef,
  canScrollPrev,
  canScrollNext,
  onPrev,
  onNext,
}: SlideServerProps) {
  return (
    <section className="w-full bg-[#E0F2FE] overflow-hidden">
      <Block>
        <div className="mx-auto w-full max-w-page px-5 @lg:px-8">
          <h2
            className="font-sans font-normal text-[40px] @lg:text-[64px] leading-[1.2] @lg:leading-[1.1] tracking-[-1.2px] @lg:tracking-[-1.92px] text-brand-text-button text-center @lg:text-left [&_i]:font-serif [&_i]:italic [&_i]:text-[44px] @lg:[&_i]:text-[70px] [&_i]:leading-[1.1] [&_i]:tracking-[-0.88px] @lg:[&_i]:tracking-[-1.4px]"
            dangerouslySetInnerHTML={{ __html: title }}
          />

          <div className="overflow-hidden @lg:mt-14" ref={emblaRef}>
            <div className="flex gap-8 @lg:gap-16">
              {items.map((slide, i) => (
                <SlideItem key={i} {...slide} />
              ))}
            </div>
          </div>

          <div className="flex justify-end mt-6 @lg:grid @lg:grid-cols-[559px_1fr] @lg:gap-16 @lg:-translate-y-full @lg:mt-0">
            <div className="flex gap-4 @lg:col-start-2">
              <NavButtons
                canScrollPrev={canScrollPrev}
                canScrollNext={canScrollNext}
                onPrev={onPrev}
                onNext={onNext}
              />
            </div>
          </div>
        </div>
      </Block>
    </section>
  );
}

function NavButtons({
  canScrollPrev,
  canScrollNext,
  onPrev,
  onNext,
}: {
  canScrollPrev?: boolean;
  canScrollNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  return (
    <>
      <button
        onClick={onPrev}
        disabled={!canScrollPrev}
        aria-label="Previous slide"
        className={clsx(
          'w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 bg-brand-muted-light',
          canScrollPrev ? 'hover:brightness-90 cursor-pointer' : 'opacity-60 cursor-not-allowed'
        )}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={onNext}
        disabled={!canScrollNext}
        aria-label="Next slide"
        className={clsx(
          'w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 bg-brand-muted-light',
          canScrollNext ? 'hover:brightness-90 cursor-pointer' : 'opacity-60 cursor-not-allowed'
        )}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </>
  );
}
